// src/components/builder/MediaPicker.js
//
// Searchable picker over the files already in Firebase Storage, backed by the
// mediaLibrary index that functions/mediaIndex.js builds.
//
// Drop it beside the existing videoUrl / musicUrl inputs in LayoutBuilder:
//
//   <MediaPicker
//     type="video"
//     topicSlug={topic.slug}
//     onSelect={(url) => handleTopicChange(index, "videoUrl", url)}
//   />
//
// The upload button stays — this is for reusing what exists, not replacing
// the ability to add something new.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit as fsLimit,
  query,
  where,
} from "firebase/firestore";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { db, storage } from "../../firebase/firebaseConfig";

const PAGE_SIZE = 40;

// Folders read straight from Storage, per media type. Same folders the
// indexer (functions/mediaIndex.js) walks; videos-previous stays out.
const STORAGE_FOLDERS = {
  video: ["videos"],
  music: ["music", "music-math"],
};

const MEDIA_RE = /\.(mp4|mov|webm|m4v|mp3|wav|m4a|ogg)$/i;

/** Same token rules as the indexer, so search behaves the same either way. */
const tokensFor = (fileName) => {
  const base = fileName.replace(/\.[^.]+$/, "");
  const cleaned = base.replace(/[_\-.]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  const out = new Set(cleaned.split(/\s+/).filter(Boolean));
  cleaned.split(/\s+/).forEach((chunk) =>
    chunk
      .split(/(\d+)/)
      .map((p) => p.trim())
      .filter((p) => p.length > 1)
      .forEach((p) => out.add(p))
  );
  out.add(base.toLowerCase());
  return Array.from(out);
};

/**
 * Lists the files in Storage itself — always current, so a file saved a
 * minute ago (a new upload, a Sing-along studio video) shows up without
 * anyone re-running the indexer. Goes one folder level deep, matching
 * videos/math-videos/ etc.
 */
const listStorageFolder = async (folder) => {
  const top = await listAll(ref(storage, folder));
  const nested = await Promise.all(
    top.prefixes.map((p) => listAll(p).catch(() => ({ items: [] })))
  );
  return [...top.items, ...nested.flatMap((n) => n.items)]
    .filter((item) => MEDIA_RE.test(item.name))
    .map((item) => ({
      id: item.fullPath.replace(/[/.]/g, "_"),
      name: item.fullPath.slice(folder.length + 1),
      path: item.fullPath,
      folder,
      tokens: tokensFor(item.name),
    }));
};

export default function MediaPicker({
  type = "video",
  topicSlug = "",
  onSelect,
  // Optional. When set, the trigger renders with this class instead of the
  // built-in grey inline style, so callers inside the builder can pass
  // "lb-btn lb-btn-sm lb-btn-soft" and get the page's blue instead of a
  // default-looking button sitting next to styled ones.
  triggerClassName = "",
  triggerLabel = "",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [folder, setFolder] = useState("all");
  const [error, setError] = useState("");
  const [resolving, setResolving] = useState(null);
  // Set once a load has been TRIED, whatever it found.
  //
  // The old trigger was "open and no items and not loading". When the
  // library came back empty, or the read failed, that stayed true the moment
  // loading finished — so it loaded again, and again, forever: the panel sat
  // on "Loading library…", the folder list never filled, and the error was
  // cleared by the next attempt before anyone could read it.
  const [attempted, setAttempted] = useState(false);
  const [note, setNote] = useState("");

  // Two sources, merged by file path:
  //   1. the mediaLibrary index (fast; has the indexer's tags)
  //   2. Storage itself (always current — includes files added since the
  //      indexer last ran)
  // Either one on its own is enough to show the list.
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setNote("");

    const [indexResult, storageResult] = await Promise.allSettled([
      getDocs(
        query(
          collection(db, "mediaLibrary"),
          where("type", "==", type),
          where("archived", "==", false),
          fsLimit(2000)
        )
      ).then((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      Promise.all((STORAGE_FOLDERS[type] || []).map(listStorageFolder)).then((lists) => lists.flat()),
    ]);

    const byPath = new Map();
    if (indexResult.status === "fulfilled") {
      indexResult.value.forEach((item) => byPath.set(item.path || item.id, item));
    } else {
      console.error("Media index load failed:", indexResult.reason);
    }
    let added = 0;
    if (storageResult.status === "fulfilled") {
      storageResult.value.forEach((item) => {
        if (!byPath.has(item.path)) {
          byPath.set(item.path, item);
          added += 1;
        }
      });
    } else {
      console.error("Storage listing failed:", storageResult.reason);
    }

    const merged = Array.from(byPath.values());
    setItems(merged);

    if (indexResult.status === "rejected" && storageResult.status === "rejected") {
      setError(
        `Couldn't load the ${type} library (${
          storageResult.reason?.code || indexResult.reason?.code || "unknown error"
        }). Check you're signed in as an admin, then press Retry.`
      );
    } else if (!merged.length) {
      setNote(`No ${type} files found.`);
    } else if (indexResult.status === "fulfilled" && added > 0) {
      setNote(`${added} newer file${added === 1 ? "" : "s"} found in Storage that the search index doesn't have yet — included below.`);
    }

    setAttempted(true);
    setLoading(false);
  }, [type]);

  useEffect(() => {
    if (open && !attempted && !loading) load();
  }, [open, attempted, loading, load]);

  // Seed the search box with the topic's slug — for most topics the matching
  // file is named after it ("force" -> force.mp4, force2025song.mp3), so the
  // common case becomes confirming a suggestion rather than searching.
  useEffect(() => {
    if (open && topicSlug && !term) setTerm(topicSlug);
  }, [open, topicSlug, term]);

  const folders = useMemo(
    () => ["all", ...Array.from(new Set(items.map((i) => i.folder))).sort()],
    [items]
  );

  const results = useMemo(() => {
    const needle = term.trim().toLowerCase();
    let list = items;

    if (folder !== "all") list = list.filter((i) => i.folder === folder);

    if (needle) {
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(needle) ||
          (i.tokens || []).some((t) => t.includes(needle))
      );
    }

    // Exact slug matches float to the top.
    const slug = topicSlug.toLowerCase();
    return [...list]
      .sort((a, b) => {
        const aExact = slug && a.name.toLowerCase().startsWith(slug) ? 0 : 1;
        const bExact = slug && b.name.toLowerCase().startsWith(slug) ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        return a.name.localeCompare(b.name);
      })
      .slice(0, PAGE_SIZE);
  }, [items, term, folder, topicSlug]);

  const choose = async (item) => {
    setResolving(item.id);
    try {
      // The index stores the path, not a URL — download tokens are per-file
      // and minting them all during indexing would be slow and wasteful.
      const url = await getDownloadURL(ref(storage, item.path));
      onSelect(url);
      setOpen(false);
    } catch (err) {
      console.error("Could not resolve download URL:", err);
      setError(`Couldn't get a URL for ${item.name}.`);
    }
    setResolving(null);
  };

  if (!open) {
    return (
      <button
        type="button"
        className={triggerClassName || undefined}
        style={triggerClassName ? undefined : s.trigger}
        onClick={() => setOpen(true)}
      >
        {triggerLabel || `Choose existing ${type}`}
      </button>
    );
  }

  return (
    <div style={s.panel}>
      <div style={s.head}>
        <strong style={s.title}>
          {type === "video" ? "Video library" : "Music library"}
        </strong>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            type="button"
            style={s.refresh}
            onClick={load}
            disabled={loading}
            title="Load the list again (picks up files saved since you opened it)"
          >
            ↻ Refresh
          </button>
          <button type="button" style={s.close} onClick={() => setOpen(false)}>
            ✕
          </button>
        </span>
      </div>

      <div style={s.controls}>
        <input
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search by file name…"
          style={s.search}
        />
        <select
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          style={s.folder}
        >
          {folders.map((f) => (
            <option key={f} value={f}>
              {f === "all" ? "All folders" : f}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p style={s.error}>
          {error}{" "}
          <button type="button" style={s.refresh} onClick={load} disabled={loading}>
            Retry
          </button>
        </p>
      )}
      {note && !error && !loading && <p style={s.meta}>{note}</p>}
      {loading && <p style={s.meta}>Loading library…</p>}

      {!loading && !error && (
        <>
          <p style={s.meta}>
            {results.length === 0
              ? "No matches."
              : `${results.length}${results.length === PAGE_SIZE ? "+" : ""} of ${items.length} files`}
          </p>
          <ul style={s.list}>
            {results.map((item) => (
              <li key={item.id} style={s.item}>
                <span style={s.name}>{item.name}</span>
                <span style={s.folderTag}>{item.folder}</span>
                <button
                  type="button"
                  style={s.use}
                  disabled={resolving === item.id}
                  onClick={() => choose(item)}
                >
                  {resolving === item.id ? "…" : "Use"}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const s = {
  trigger: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 6,
  },
  panel: {
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: 12,
    marginTop: 8,
    background: "#fafafa",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { fontSize: 14 },
  refresh: {
    border: "1px solid #d1d5db",
    background: "#fff",
    borderRadius: 6,
    padding: "3px 9px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  close: {
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 15,
    color: "#6b7280",
  },
  controls: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" },
  search: {
    flex: "1 1 180px",
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
  },
  folder: {
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
  },
  meta: { fontSize: 12, color: "#6b7280", margin: "10px 0 6px" },
  error: { fontSize: 12, color: "#991b1b", margin: "10px 0 0", lineHeight: 1.5 },
  list: { listStyle: "none", padding: 0, margin: 0, maxHeight: 260, overflowY: "auto" },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 0",
    borderTop: "1px solid #eee",
    fontSize: 13,
  },
  name: { flex: "1 1 auto", overflowWrap: "anywhere" },
  folderTag: { fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" },
  use: {
    padding: "5px 12px",
    borderRadius: 6,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
};
